import { useRef, useState } from "react";
import type { KeyboardEvent, PointerEvent } from "react";
import { ChevronsLeftRight } from "lucide-react";
import { OfflineImage } from "@/components/common/offline-image/offline-image";
import { formatPhotoDate, type IssuePhoto } from "./issue-photos";

/** How far one arrow-key press moves the divider, in percent of the width. */
const KEY_STEP = 5;

function clamp(value: number): number {
  return Math.min(100, Math.max(0, value));
}

interface CompareWipeProps {
  before: IssuePhoto;
  after: IssuePhoto;
}

/**
 * Two photos laid over each other with a draggable divider: Before to its
 * left, After to its right. After lies underneath at full size and Before sits
 * on top, clipped at the divider, so both stay fitted exactly as the viewer
 * fits them. Pressing anywhere on the stage moves the divider there and keeps
 * tracking until release, so a phone user never has to hit the handle. No zoom
 * here: a drag only ever moves the divider. See [[issue-photo-viewer.spec]].
 */
export function CompareWipe({ before, after }: CompareWipeProps) {
  // Kept when the compared photos change, so swapping the second photo does
  // not throw away where the user had the divider.
  const [position, setPosition] = useState(50);
  const stageRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);

  const moveTo = (clientX: number) => {
    const rect = stageRef.current?.getBoundingClientRect();
    if (!rect || rect.width <= 0) return;
    setPosition(clamp(((clientX - rect.left) / rect.width) * 100));
  };

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    draggingRef.current = true;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // jsdom / unsupported: capture is a nicety, not required.
    }
    moveTo(e.clientX);
  };

  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (draggingRef.current) moveTo(e.clientX);
  };

  const endDrag = () => {
    draggingRef.current = false;
  };

  const onHandleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const next =
      e.key === "ArrowLeft"
        ? position - KEY_STEP
        : e.key === "ArrowRight"
          ? position + KEY_STEP
          : e.key === "Home"
            ? 0
            : e.key === "End"
              ? 100
              : null;
    if (next === null) return;
    e.preventDefault();
    setPosition(clamp(next));
  };

  const rounded = Math.round(position);

  return (
    <div
      ref={stageRef}
      role="group"
      aria-label="Before and after comparison"
      className="relative h-full w-full cursor-ew-resize select-none overflow-hidden"
      style={{ touchAction: "none", WebkitTouchCallout: "none" }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      <OfflineImage
        assetKey=""
        src={after.src}
        alt="After"
        draggable={false}
        className="absolute inset-0 h-full w-full object-contain"
      />
      <div
        className="absolute inset-0"
        style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}
        data-testid="compare-before-layer"
      >
        <OfflineImage
          assetKey=""
          src={before.src}
          alt="Before"
          draggable={false}
          className="absolute inset-0 h-full w-full object-contain"
        />
      </div>

      <WipeChip side="left" label="Before" date={before.date} />
      <WipeChip side="right" label="After" date={after.date} />

      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 w-0.5 -translate-x-1/2 bg-white shadow-[0_0_6px_rgba(0,0,0,0.5)]"
        style={{ left: `${position}%` }}
      />
      <div
        role="slider"
        tabIndex={0}
        aria-label="Before and after divider"
        aria-orientation="horizontal"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={rounded}
        aria-valuetext={`${rounded}% before`}
        onKeyDown={onHandleKeyDown}
        className="absolute top-1/2 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-black shadow-lg outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-black"
        style={{ left: `${position}%` }}
      >
        <ChevronsLeftRight className="h-5 w-5" aria-hidden="true" />
      </div>
    </div>
  );
}

function WipeChip({
  side,
  label,
  date,
}: {
  side: "left" | "right";
  label: string;
  date: string | null;
}) {
  const formatted = formatPhotoDate(date);
  return (
    <span
      className={`pointer-events-none absolute top-3 rounded-full bg-black/60 px-2.5 py-1 text-xs font-medium text-white ${
        side === "left" ? "left-3" : "right-3"
      }`}
    >
      {formatted ? `${label} · ${formatted}` : label}
    </span>
  );
}
